import React from 'react';
import clsx from 'clsx';
import { Loader2 } from 'lucide-react';

// Consolidates the button treatments already in use (primary blue, dark
// "Acknowledge"-style, success emerald, warning amber, danger red, and a
// bordered ghost/outline variant) — every page currently hand-writes these
// class strings per button, with small drift (rounded-lg vs rounded-xl,
// font-semibold vs font-bold, differing padding) between otherwise-identical
// actions. Same loading-spinner-replaces-icon pattern already used for
// Acknowledge/Initiate/Pause is built in via `loading`.
const VARIANT_CLASSES = {
  primary:      'bg-blue-700 hover:bg-blue-800 text-white',
  primaryLight: 'bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200', // secondary action on a light-blue tint (e.g. "Insert template text")
  outline:      'bg-white hover:bg-blue-50 text-blue-700 border-2 border-blue-600', // white bg, bold blue border — a secondary action that still reads as "primary-ish" (e.g. "Save as Pending" beside "Approve & Broadcast")
  dark:         'bg-gray-700 hover:bg-gray-800 text-white',
  success:      'bg-emerald-600 hover:bg-emerald-700 text-white',
  warning:      'bg-amber-500 hover:bg-amber-600 text-white',
  danger:       'bg-danger-600 hover:bg-danger-700 text-white',
  dangerLight:  'bg-danger-100 hover:bg-danger-200 text-danger-700', // secondary/less-prominent destructive action (e.g. "Cancel" next to a primary control)
  ghost:        'text-gray-500 hover:text-gray-900 hover:bg-gray-100 border border-gray-200',
};

const SIZE_CLASSES = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-3.5 py-2 text-sm',
  lg: 'px-5 py-2.5 text-sm',
  // The full-width "hero" submit CTA at the bottom of a form (Ad-Hoc,
  // Announce) — bigger padding/text than any list-page action button.
  xl: 'px-5 py-4 text-base',
};

const ICON_SIZES = { sm: 12, md: 13, lg: 14, xl: 18 };

export default function Button({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  loading = false,
  fullWidth = false,
  disabled = false,
  className = '',
  children,
  ...rest
}) {
  const iconSize = ICON_SIZES[size] || ICON_SIZES.md;
  return (
    <button
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 rounded-xl font-bold transition-colors disabled:opacity-60',
        VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary,
        SIZE_CLASSES[size] || SIZE_CLASSES.md,
        fullWidth && 'w-full',
        className
      )}
      {...rest}
    >
      {loading ? <Loader2 size={iconSize} className="animate-spin" /> : Icon && <Icon size={iconSize} />}
      {children}
    </button>
  );
}
